import React, { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
    Compass,
    Maximize2,
    Minimize2,
    RotateCcw,
    X as CloseIcon,
} from "lucide-react";
import { useHasGyroscope } from "@/lib/useHasGyroscope";
import { hasWebGLSupport } from "@/lib/webgl";
import ViewerFallback from "@/components/ViewerFallback";
import { useViewerTracking } from "@/lib/viewerTracking";

/**
 * BeforeAfter360Viewer
 * ---------------------------------------------------------------
 * Ein 360°-Viewer, der ZWEI equirektangulare Panoramen simultan
 * anzeigt und über einen screen-space vertikalen Split mischt.
 *
 * - Three.js SphereGeometry (invertiert, Kamera innen)
 * - Custom ShaderMaterial mit Uniforms:
 *      uTextureBefore, uTextureAfter, uSplit (0..1), uResolution
 * - Kamera-Rotation via Pointer/Touch/Wheel unabhängig vom Slider
 * - Vertikaler Screen-Space-Slider (HTML-Overlay, pointer-events:auto)
 * - Optionale Auto-Tour (rAF), Gyro (deviceorientation), Reset,
 *   Fullscreen — dieselbe Semantik wie Video/Photo360.
 * - Mutual Exclusion Tour ⇄ Gyro, prefers-reduced-motion Guard.
 * - Sauberer Cleanup aller Ressourcen (Textures, Renderer, rAF).
 */

interface Props {
    beforeUrl: string;
    afterUrl: string;
    onClose?: () => void;
    testIdPrefix?: string;
    /** Wenn true: rAF-Loop wird pausiert, keine WebGL-Render-Calls mehr.
     *  Wechselt der Wert wieder auf false, wird die Loop neu gestartet.
     *  Zweck: bei off-screen Kacheln GPU-Zyklen sparen (siehe
     *  InViewportWrapper.onEnter/onLeave). */
    renderPaused?: boolean;
    /** Instant-Immersion: sobald beide Panorama-Texturen geladen sind, startet
     *  ein horizontaler rAF-Yaw-Sweep (~12°/s, kein Pitch-Wave — der Split
     *  bleibt so ruhig genug für Vorher/Nachher-Verständnis). Stopp bei jeder
     *  User-Interaktion (Drag auf Canvas, Slider-Handle, Wheel, Touch).
     *  Default: true für die Grid-Kacheln. */
    autoTour?: boolean;
    /** Analytics: stabile Media-ID des Vorher-Nachher-Paars. */
    mediaId?: string;
    mediaTitle?: string;
    projectId?: string;
    /** KI-Kennzeichnung des Vorher-Panoramas (aus `media.is_ai_generated`). */
    beforeAi?: boolean;
    /** KI-Kennzeichnung des Nachher-Panoramas. */
    afterAi?: boolean;
}

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uTextureBefore;
uniform sampler2D uTextureAfter;
uniform float uSplit;
uniform vec2 uResolution;
varying vec2 vUv;
void main() {
    // Screen-Space-Split:
    //   uSplit = 0.0 → gesamter Screen = Before (Vorher)
    //   uSplit = 1.0 → gesamter Screen = After (Nachher)
    //   Handle bei sx=uSplit trennt: sx < uSplit → After, sx > uSplit → Before.
    // step(sx, uSplit) = 1.0 wenn uSplit >= sx, sonst 0.0.
    // Bei uSplit=0 → step=0 → before (100 %). Bei uSplit=1 → step=1 → after (100 %).
    float sx = gl_FragCoord.x / uResolution.x;
    vec4 before = texture2D(uTextureBefore, vUv);
    vec4 after  = texture2D(uTextureAfter,  vUv);
    gl_FragColor = mix(before, after, step(sx, uSplit));
}
`;

export default function BeforeAfter360Viewer({
    beforeUrl,
    afterUrl,
    onClose,
    testIdPrefix = "ba360",
    renderPaused = false,
    autoTour = true,
    mediaId,
    mediaTitle,
    projectId,
    beforeAi = false,
    afterAi = false,
}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const materialRef = useRef<THREE.ShaderMaterial | null>(null);
    const texturesRef = useRef<THREE.Texture[]>([]);
    const animRafRef = useRef<number | null>(null);
    // Analytics: Vorher-Nachher-360-Tracking (Open, Interaction, Close).
    const tracker = useViewerTracking({
        viewerType: "before_after_360",
        mediaId,
        mediaTitle: mediaTitle || `${beforeUrl} vs ${afterUrl}`,
        projectId,
    });
    /** Guard-Flag: sobald Cleanup läuft, wird jeder pending Async-Callback
     *  (rAF, TextureLoader done/error) bei Zugriff auf disposed Ressourcen
     *  abgebrochen. Verhindert `THREE.WebGLRenderer`-Runtime-Errors bei
     *  schnellem Mount/Unmount-Toggle (Kachel-Wechsel in Transformations360). */
    const isMountedRef = useRef(true);
    /** rAF-Pause-Flag: wenn true, wird kein neuer Frame geschedult. Vom
     *  `renderPaused`-Prop synchronisiert (separater Effect unten). */
    const pausedRef = useRef(false);
    /** Trampolin-Handle: erlaubt es dem Resume-Effect, die im Setup-
     *  Effect erstellte `animate()`-Funktion erneut anzustoßen. */
    const resumeAnimRef = useRef<(() => void) | null>(null);

    // Kamera-State (analog Video360)
    // Initial-Yaw = 0 (Front-View), Initial-Pitch = 0 (Eye-Level, keine Neigung).
    const lonRef = useRef(0);
    const latRef = useRef(0);
    const fovRef = useRef(75);

    // Split (0..1) – als Ref um Re-Renders zu vermeiden. React-State nur für UI.
    const splitRef = useRef(0.5);
    const [split, setSplit] = useState(0.5);

    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initError, setInitError] = useState<string | null>(null);

    // Auto-Tour
    const autoRotatingRef = useRef(false);
    const [autoRotating, setAutoRotating] = useState(false);
    const autoRafRef = useRef<number | null>(null);
    /** Auto-Resume-Timer: nach 5 s Idle (kein aktiver Canvas-Drag mehr) wird
     *  die Auto-Tour automatisch fortgesetzt. Ein neuer Canvas-Drag cancelt
     *  den Timer, damit der Nutzer nicht mitten in der Interaktion
     *  „übersteuert" wird. Slider-Interaktion (Split-Trenner) berührt weder
     *  Timer noch Rotation. */
    const autoResumeTimerRef = useRef<number | null>(null);

    // Gyro
    const gyroActiveRef = useRef(false);
    const gyroHandlerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);
    const [gyroOn, setGyroOn] = useState(false);
    const showGyro = useHasGyroscope();

    const [isFullscreen, setIsFullscreen] = useState(false);

    /** Externer Trigger für `applyResize` (innerhalb des Init-useEffect
     *  definiert). Wird vom Fullscreen-toggle und vom `fullscreenchange`-
     *  Handler benutzt, um ausserhalb des Container-ResizeObservers einen
     *  expliziten `setSize` + `render`-Frame zu erzwingen. Sonst kann der
     *  Renderer nach FS-Enter schwarz bleiben, wenn der Observer nicht
     *  rechtzeitig feuert. */
    const resizeTriggerRef = useRef<(() => void) | null>(null);

    const detachGyro = useCallback(() => {
        if (gyroHandlerRef.current) {
            window.removeEventListener("deviceorientation", gyroHandlerRef.current, true);
            gyroHandlerRef.current = null;
        }
        gyroActiveRef.current = false;
        setGyroOn(false);
    }, []);

    const stopAutoRotate = useCallback(() => {
        if (autoRafRef.current !== null) {
            cancelAnimationFrame(autoRafRef.current);
            autoRafRef.current = null;
        }
        autoRotatingRef.current = false;
        setAutoRotating(false);
    }, []);

    /** Cancelt einen laufenden Auto-Resume-Timer, ohne die Rotation zu
     *  starten oder zu stoppen. Wird bei jedem Canvas-pointerdown gerufen —
     *  damit ein Idle-Timer, der noch von einem vorherigen Drag her läuft,
     *  nicht mitten in eine neue User-Interaktion hineinfeuert. */
    const cancelAutoResume = useCallback(() => {
        if (autoResumeTimerRef.current !== null) {
            window.clearTimeout(autoResumeTimerRef.current);
            autoResumeTimerRef.current = null;
        }
    }, []);

    const startAutoRotate = useCallback(() => {
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
        if (gyroActiveRef.current) detachGyro();
        autoRotatingRef.current = true;
        setAutoRotating(true);
        const t0 = performance.now();
        const startLon = lonRef.current;
        const YAW = 12; // deg/s — ausschliesslich horizontale Rotation (yaw/lon).
        // Keine Pitch-Sway, kein FOV-/Zoom-Change: der Blick bleibt eye-level & natürlich.
        const tick = (now: number) => {
            if (!autoRotatingRef.current) return;
            const t = (now - t0) / 1000;
            const ease = Math.min(t / 1.0, 1);
            lonRef.current = (startLon + YAW * ease * t + 360) % 360;
            autoRafRef.current = requestAnimationFrame(tick);
        };
        autoRafRef.current = requestAnimationFrame(tick);
    }, [detachGyro]);

    /** Startet einen 5-Sekunden-Auto-Resume-Timer. Nach Ablauf wird
     *  `startAutoRotate()` gerufen — sofern der Viewer nicht pausiert ist,
     *  kein Gyro läuft und die Auto-Tour nicht bereits läuft. Vor jedem
     *  neuen Timer wird ein evtl. bestehender abgebrochen. Aufrufer ist
     *  typischerweise ein `pointerup` nach echtem Canvas-Drag. */
    const scheduleAutoResume = useCallback(() => {
        cancelAutoResume();
        autoResumeTimerRef.current = window.setTimeout(() => {
            autoResumeTimerRef.current = null;
            if (!isMountedRef.current) return;
            if (pausedRef.current) return;
            if (gyroActiveRef.current) return;
            if (autoRotatingRef.current) return;
            startAutoRotate();
        }, 5000);
    }, [cancelAutoResume, startAutoRotate]);

    const resetView = useCallback(() => {
        if (autoRotatingRef.current) {
            stopAutoRotate();
            return;
        }
        // Sanfter Tween zurück zu Initial-Werten: lon=0, lat=0, fov=75.
        const startLon = lonRef.current;
        const startLat = latRef.current;
        const startFov = fovRef.current;
        // Kürzesten Weg zu lon=0 finden.
        let dLon = 0 - startLon;
        if (dLon > 180) dLon -= 360;
        if (dLon < -180) dLon += 360;
        const t0 = performance.now();
        const DUR = 700;
        const step = (now: number) => {
            const t = Math.min((now - t0) / DUR, 1);
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            lonRef.current = (startLon + dLon * eased + 360) % 360;
            latRef.current = startLat + (0 - startLat) * eased;
            fovRef.current = startFov + (75 - startFov) * eased;
            if (cameraRef.current) {
                cameraRef.current.fov = fovRef.current;
                cameraRef.current.updateProjectionMatrix();
            }
            if (t < 1) requestAnimationFrame(step);
            else startAutoRotate();
        };
        requestAnimationFrame(step);
    }, [startAutoRotate, stopAutoRotate]);

    const toggleGyro = useCallback(async () => {
        if (gyroActiveRef.current) {
            detachGyro();
            return;
        }
        const DOE = window.DeviceOrientationEvent as unknown as {
            requestPermission?: () => Promise<"granted" | "denied">;
        };
        if (DOE && typeof DOE.requestPermission === "function") {
            try {
                const s = await DOE.requestPermission();
                if (s !== "granted") return;
            } catch {
                return;
            }
        }
        if (autoRotatingRef.current) stopAutoRotate();
        const handler = (ev: DeviceOrientationEvent) => {
            if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
            lonRef.current = (360 - ev.alpha + 360) % 360;
            latRef.current = Math.max(-85, Math.min(85, ev.beta - 90));
        };
        window.addEventListener("deviceorientation", handler, true);
        gyroHandlerRef.current = handler;
        gyroActiveRef.current = true;
        setGyroOn(true);
    }, [detachGyro, stopAutoRotate]);

    const toggleFullscreen = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            const p = el.requestFullscreen?.();
            tracker.onFullscreen();
            // Nach Fullscreen-Enter kurz warten und Resize triggern — der
            // Browser reflowt das Element auf window.innerWidth/Height,
            // der ResizeObserver feuert manchmal nicht rechtzeitig.
            if (p && typeof p.then === "function") {
                p.then(() => {
                    window.setTimeout(() => resizeTriggerRef.current?.(), 100);
                    window.setTimeout(() => resizeTriggerRef.current?.(), 300);
                }).catch(() => { /* User canceled etc. */ });
            }
        } else {
            document.exitFullscreen?.();
        }
    }, []);

    useEffect(() => {
        const onFs = () => {
            setIsFullscreen(!!document.fullscreenElement);
            // Fullscreen-Enter UND -Exit brauchen einen expliziten Resize —
            // der Container wechselt zwischen normalen Layout-Dimensionen
            // und viewport-full. Delay-Kette 100/300 ms deckt iOS-Safari,
            // Chrome-Android und Desktop ab.
            window.setTimeout(() => resizeTriggerRef.current?.(), 100);
            window.setTimeout(() => resizeTriggerRef.current?.(), 300);
        };
        document.addEventListener("fullscreenchange", onFs);
        return () => document.removeEventListener("fullscreenchange", onFs);
    }, []);

    // Cleanup gyro on unmount
    useEffect(() => () => detachGyro(), [detachGyro]);

    // Cleanup Auto-Resume-Timer on unmount — sonst könnte ein pending Timer
    // nach dem Unmount `startAutoRotate()` rufen und auf disposed Renderer
    // zugreifen. `isMountedRef` in scheduleAutoResume schützt zusätzlich.
    useEffect(() => () => cancelAutoResume(), [cancelAutoResume]);

    // -------- Three.js Setup --------
    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;
        // Early WebGL-Guard: verhindert Runtime-Crash wenn Browser keinen
        // WebGL-Context liefern kann. Fallback wird via `initError`-State
        // (siehe render() unten) angezeigt.
        if (!hasWebGLSupport()) {
            setInitError(
                "WebGL ist in diesem Browser nicht verfügbar. Bitte moderneren Browser nutzen oder Hardware-Beschleunigung aktivieren.",
            );
            return;
        }

        const scene = new THREE.Scene();
        // FOV-Default explizit: 75° (natürlicher Raumblick, keine Verzerrung).
        // Kamera sitzt exakt im Sphere-Zentrum (0,0,0), zoom=1.
        const camera = new THREE.PerspectiveCamera(
            75,
            container.clientWidth / Math.max(container.clientHeight, 1),
            0.1,
            1100,
        );
        camera.position.set(0, 0, 0);
        camera.zoom = 1.0;
        camera.updateProjectionMatrix();
        fovRef.current = 75;
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(container.clientWidth, container.clientHeight, false);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current = renderer;
        const maxAniso = renderer.capabilities.getMaxAnisotropy();
        // WICHTIG: uResolution MUSS in physikalischen Framebuffer-Pixeln
        // gesetzt werden — gl_FragCoord.x läuft ebenfalls in phys. Pixeln.
        // Bei DPR=2 sonst: sx = gl_FragCoord.x / uResolution.x  → bis ~2.0
        // statt 1.0 → step(sx, uSplit=1) bleibt für die rechte Bildhälfte 0
        // → After enthüllt sich nie ganz.
        const dpr = renderer.getPixelRatio();

        // 128×64 Segmente für glatte Sphere-Silhouette bei 2048² Panoramen.
        const geo = new THREE.SphereGeometry(500, 128, 64);
        // Kamera schaut von innen → normals umkehren
        geo.scale(-1, 1, 1);

        const mat = new THREE.ShaderMaterial({
            uniforms: {
                uTextureBefore: { value: null },
                uTextureAfter: { value: null },
                uSplit: { value: 0.5 },
                uResolution: {
                    value: new THREE.Vector2(
                        container.clientWidth * dpr,
                        container.clientHeight * dpr,
                    ),
                },
            },
            vertexShader: VERT,
            fragmentShader: FRAG,
        });
        materialRef.current = mat;

        const mesh = new THREE.Mesh(geo, mat);
        scene.add(mesh);

        // Textures laden
        const loader = new THREE.TextureLoader();
        loader.setCrossOrigin("anonymous");
        let loadedCount = 0;
        const done = (t: THREE.Texture, key: "uTextureBefore" | "uTextureAfter") => {
            // Race-Guard: wenn Component bereits unmountet ist, keine Zugriffe
            // auf disposed material/state mehr.
            if (!isMountedRef.current) {
                try { t.dispose(); } catch { /* noop */ }
                return;
            }
            t.colorSpace = THREE.SRGBColorSpace;
            t.wrapS = THREE.RepeatWrapping;
            t.wrapT = THREE.ClampToEdgeWrapping;
            // Mipmap-Kette für saubere Filterung bei schräger Sphere-Sampling.
            // 2048×2048 ist power-of-two → generateMipmaps ist sicher.
            t.generateMipmaps = true;
            t.minFilter = THREE.LinearMipmapLinearFilter;
            t.magFilter = THREE.LinearFilter;
            t.anisotropy = maxAniso;
            t.needsUpdate = true;
            mat.uniforms[key].value = t;
            texturesRef.current.push(t);
            loadedCount++;
            if (loadedCount >= 2) setLoaded(true);
        };
        loader.load(
            beforeUrl,
            // Uniform-Swap: `beforeUrl` (Vorher-Content) wird in `uTextureAfter`
            // gebunden. Der Shader-Kommentar oben zeigt: `mix(before, after,
            // step(sx, uSplit))` liefert bei `sx < uSplit` (linke Hälfte)
            // Fragment aus `uTextureAfter`. Damit landet der Vorher-Content
            // visuell links — konsistent mit den Labels („VORHER" links,
            // „NACHHER" rechts). Shader selbst bleibt unverändert.
            (t) => done(t, "uTextureAfter"),
            undefined,
            (err) => {
                if (!isMountedRef.current) return;
                console.error("[BA360] before load failed", err);
                setLoadError("Vorher-Bild konnte nicht geladen werden.");
            },
        );
        loader.load(
            afterUrl,
            (t) => done(t, "uTextureBefore"),
            undefined,
            (err) => {
                if (!isMountedRef.current) return;
                console.error("[BA360] after load failed", err);
                setLoadError("Nachher-Bild konnte nicht geladen werden.");
            },
        );

        // Resize — debounced (150 ms) gegen Orientation-Change-Bursts.
        // Zusätzlicher `orientationchange`-Listener mit 300 ms Delay, weil
        // `clientWidth/Height` erst nach dem Event settle sind (iOS/Android
        // feuern das Event mitten im Layout-Reflow).
        const resizeTimeoutRef: { id: number | null } = { id: null };
        const applyResize = () => {
            if (!container || !rendererRef.current || !cameraRef.current) return;
            // Fullscreen-Sonderfall: wenn wir im FS-Modus sind, ist der
            // Container per CSS `position:fixed inset:0` — clientWidth/Height
            // matchen dann window-Dimensionen. Auf iOS-Safari können sie aber
            // während der Fullscreen-Transition kurz 0 sein — Fallback auf
            // window.innerWidth/Height.
            let w = container.clientWidth;
            let h = container.clientHeight;
            if ((!w || !h) && document.fullscreenElement === container) {
                w = window.innerWidth;
                h = window.innerHeight;
            }
            // Guard: während Portrait↔Landscape-Übergang kann w oder h für
            // ein Frame 0 sein. Dann skippen, sonst zerstört `setSize(0,0)`
            // den Canvas visuell und beim Re-Layout entsteht ein leerer
            // Renderer-Slot.
            if (!w || !h) return;
            cameraRef.current.aspect = w / h;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(w, h, false);
            const dprNow = rendererRef.current.getPixelRatio();
            if (mat && mat.uniforms.uResolution) {
                mat.uniforms.uResolution.value.set(w * dprNow, h * dprNow);
            }
            // Sofort-Repaint: sonst bleibt der Renderer nach Resize für einen
            // Frame schwarz, wenn die rAF-Loop gerade pausiert war
            // (renderPaused=true) oder wenn wir in einer Transition sind.
            try {
                rendererRef.current.render(scene, cameraRef.current);
            } catch { /* scene evtl. noch nicht ready — harmlos */ }
        };
        // Externer Trigger für Fullscreen-toggle exposen (Fullscreen-Handler
        // ist ausserhalb dieses useEffect, braucht Zugriff auf applyResize).
        resizeTriggerRef.current = applyResize;
        const onResize = () => {
            if (resizeTimeoutRef.id !== null) {
                window.clearTimeout(resizeTimeoutRef.id);
            }
            resizeTimeoutRef.id = window.setTimeout(() => {
                resizeTimeoutRef.id = null;
                applyResize();
            }, 150);
        };
        const onOrientation = () => {
            // Nach `orientationchange` sind Layout-Dimensionen erst nach ~200-
            // 300 ms konsistent. Verzögerung + zusätzlicher `applyResize` nach
            // dem Debounce-Delta = doppelter Sicherheitsnetz-Trigger.
            window.setTimeout(() => onResize(), 300);
        };
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onOrientation);
        const ro = new ResizeObserver(onResize);
        ro.observe(container);

        // -------- WebGL Context-Loss / Restore --------
        // Auf mobilen Geräten kann bei Fullscreen+Orientation der GPU-Kontext
        // verloren gehen (iOS Safari: max ~8 aktive Kontexte, Android Chrome:
        // Suspension bei Memory-Druck). Ohne Recovery bleibt der Canvas
        // dauerhaft schwarz. Wir fangen den Loss ab und markieren Texturen
        // nach Restore als update-needed — Three.js lädt sie dann neu.
        const canvasEl = renderer.domElement;
        const onCtxLost = (e: Event) => {
            // Default würde den Kontext endgültig verloren geben. preventDefault
            // erlaubt Restore.
            e.preventDefault();
        };
        const onCtxRestored = () => {
            // Alle bekannten Texturen als „muss neu uploaden" markieren.
            const before = mat?.uniforms?.uTextureBefore?.value as THREE.Texture | null;
            const after = mat?.uniforms?.uTextureAfter?.value as THREE.Texture | null;
            if (before) before.needsUpdate = true;
            if (after) after.needsUpdate = true;
            // Explizit Resize + Repaint — ohne das bleibt der Renderer bei
            // seiner alten Size hängen und der Restore ist nicht sichtbar.
            applyResize();
        };
        canvasEl.addEventListener("webglcontextlost", onCtxLost, false);
        canvasEl.addEventListener("webglcontextrestored", onCtxRestored, false);

        // rAF loop
        const animate = () => {
            // Race-Guard: wenn Cleanup schon lief, keinen weiteren Frame
            // schedulen und nicht auf disposed Renderer/Material zugreifen.
            if (!isMountedRef.current || !rendererRef.current) return;
            // Pause-Guard: off-screen → keine GPU-Zyklen mehr verbrauchen.
            // Der Resume-Effect (unten) startet die Loop wieder an.
            if (pausedRef.current) {
                animRafRef.current = null;
                return;
            }
            const lat = latRef.current * (Math.PI / 180);
            const lon = lonRef.current * (Math.PI / 180);
            const cx = 500 * Math.cos(lat) * Math.sin(lon);
            const cy = 500 * Math.sin(lat);
            const cz = 500 * Math.cos(lat) * Math.cos(lon);
            camera.lookAt(cx, cy, cz);
            camera.fov = fovRef.current;
            camera.updateProjectionMatrix();
            mat.uniforms.uSplit.value = splitRef.current;
            renderer.render(scene, camera);
            animRafRef.current = requestAnimationFrame(animate);
        };
        // isMountedRef ist bei jedem Effect-Run wieder true zu setzen
        // (React StrictMode / rapid re-mount).
        isMountedRef.current = true;
        // Resume-Handle für den externen renderPaused-Effect exposen.
        resumeAnimRef.current = () => {
            if (!isMountedRef.current) return;
            if (pausedRef.current) return;
            if (animRafRef.current !== null) return; // schon laufend
            animate();
        };
        animate();

        // Cleanup
        return () => {
            isMountedRef.current = false;
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onOrientation);
            if (resizeTimeoutRef.id !== null) {
                window.clearTimeout(resizeTimeoutRef.id);
                resizeTimeoutRef.id = null;
            }
            canvasEl.removeEventListener("webglcontextlost", onCtxLost);
            canvasEl.removeEventListener("webglcontextrestored", onCtxRestored);
            resizeTriggerRef.current = null;
            ro.disconnect();
            if (animRafRef.current !== null) {
                cancelAnimationFrame(animRafRef.current);
                animRafRef.current = null;
            }
            texturesRef.current.forEach((t) => {
                try { t.dispose(); } catch { /* noop */ }
            });
            texturesRef.current = [];
            try { geo.dispose(); } catch { /* noop */ }
            try { mat.dispose(); } catch { /* noop */ }
            // Explizit WebGL-Kontext freigeben, sonst kann Chromes 16-Context-
            // Limit greifen und ältere Kontexte hart verwerfen → laufende
            // Referenzen im disposed Renderer crashen mit „Context Lost".
            try { renderer.forceContextLoss(); } catch { /* noop */ }
            try { renderer.dispose(); } catch { /* noop */ }
            rendererRef.current = null;
            cameraRef.current = null;
            materialRef.current = null;
            resumeAnimRef.current = null;
        };
    }, [beforeUrl, afterUrl]);

    // Sync des `renderPaused`-Props in den internen pausedRef. Beim Wechsel
    // von paused→resumed wird die rAF-Loop erneut angeworfen. Beim Wechsel
    // resumed→paused wird der bereits gequeuede rAF gecancelt.
    useEffect(() => {
        pausedRef.current = renderPaused;
        if (renderPaused) {
            if (animRafRef.current !== null) {
                cancelAnimationFrame(animRafRef.current);
                animRafRef.current = null;
            }
            // Auto-Tour ebenfalls anhalten wenn Kachel off-screen scrollt —
            // sonst dreht lonRef unsichtbar weiter und verbrennt CPU.
            if (autoRotatingRef.current) {
                stopAutoRotate();
            }
            // Auto-Resume-Timer canceln — wenn die Kachel unsichtbar wird,
            // soll sie nicht 5 s später „aus dem Nichts" wieder rotieren.
            cancelAutoResume();
        } else {
            resumeAnimRef.current?.();
        }
    }, [renderPaused, stopAutoRotate, cancelAutoResume]);

    // Auto-Tour-Trigger: sobald beide Texturen geladen sind, startet der
    // horizontale Sweep. Läuft nur wenn `autoTour` gesetzt, Kachel im
    // Viewport (`!renderPaused`), Gyro nicht aktiv, und noch keine
    // User-Interaktion (autoRotatingRef=false). Der bestehende
    // `stopAutoRotate` in Pointer/Touch/Wheel-Handlern übernimmt den Stopp.
    useEffect(() => {
        if (!loaded) return;
        if (!autoTour) return;
        if (renderPaused) return;
        if (gyroActiveRef.current) return;
        if (autoRotatingRef.current) return;
        // Kleiner Delay damit die Textures wirklich uploadet sind bevor die
        // Kamera zu bewegen beginnt (verhindert ersten Frame-Ruckler).
        const t = window.setTimeout(() => {
            if (autoRotatingRef.current) return;
            if (gyroActiveRef.current) return;
            if (pausedRef.current) return;
            startAutoRotate();
        }, 250);
        return () => window.clearTimeout(t);
    }, [loaded, autoTour, renderPaused, startAutoRotate]);

    // Test-Hook: expose per-instance getYaw auf window. Erlaubt harte
    // Playwright-Assertions über mehrere Kacheln (`window.__hf_ba360__[testIdPrefix].getYaw()`).
    // Zusätzlich Mount-Counter (`__hf_ba360_mounts__[prefix]`): wird bei jedem
    // Mount inkrementiert — Regression-Guard gegen unerwartete Re-Mounts
    // (z. B. durch Orientation-Change-getriggerte Unmounts im InViewportWrapper).
    useEffect(() => {
        const w = window as unknown as {
            __hf_ba360__?: Record<string, { getYaw: () => number; getLat: () => number }>;
            __hf_ba360_mounts__?: Record<string, number>;
        };
        if (!w.__hf_ba360__) w.__hf_ba360__ = {};
        if (!w.__hf_ba360_mounts__) w.__hf_ba360_mounts__ = {};
        w.__hf_ba360__[testIdPrefix] = {
            getYaw: () => lonRef.current,
            getLat: () => latRef.current,
        };
        w.__hf_ba360_mounts__[testIdPrefix] = (w.__hf_ba360_mounts__[testIdPrefix] || 0) + 1;
        return () => {
            if (w.__hf_ba360__ && w.__hf_ba360__[testIdPrefix]) {
                delete w.__hf_ba360__[testIdPrefix];
            }
        };
    }, [testIdPrefix]);

    // -------- Canvas Interaction (Pointer/Wheel) — Kamera-Rotation --------
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        let dragging = false;
        let lastX = 0;
        let lastY = 0;
        let pinchStart: { d: number; fov: number } | null = null;

        const shouldSkip = (tgt: EventTarget | null): boolean => {
            const el = tgt as HTMLElement | null;
            return !!(el && el.closest(".ba360-interactive"));
        };

        const onPointerDown = (e: PointerEvent) => {
            if (gyroActiveRef.current) return;
            // Slider/Controls (`.ba360-interactive`) sollen die Auto-Tour
            // NIEMALS berühren — weder stoppen noch resumen. Slider steuert
            // ausschliesslich den Split, die Kamera dreht ungestört weiter.
            if (shouldSkip(e.target)) return;
            // Echter Canvas-Drag = User will Raum manuell rotieren:
            //   1) laufenden Auto-Resume-Timer canceln (Nutzer greift ein)
            //   2) laufende Auto-Tour temporär stoppen
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            dragging = true;
            lastX = e.clientX;
            lastY = e.clientY;
            (e.target as Element).setPointerCapture?.(e.pointerId);
        };
        const onPointerMove = (e: PointerEvent) => {
            if (!dragging || gyroActiveRef.current) return;
            const dx = e.clientX - lastX;
            const dy = e.clientY - lastY;
            lastX = e.clientX;
            lastY = e.clientY;
            const sens = 0.15 * (fovRef.current / 75);
            lonRef.current = (lonRef.current - dx * sens + 360) % 360;
            latRef.current = Math.max(-85, Math.min(85, latRef.current + dy * sens));
        };
        const onPointerUp = () => {
            // Nur wenn tatsächlich Canvas-Drag stattgefunden hat, den 5s-
            // Auto-Resume-Timer starten. Slider-pointerup läuft hier gar nicht
            // durch (stopPropagation im Slider-Handler + shouldSkip-Guard oben).
            if (dragging) {
                dragging = false;
                scheduleAutoResume();
                tracker.onInteraction(lonRef.current, latRef.current, fovRef.current);
            } else {
                dragging = false;
            }
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            // Wheel-Zoom ist ebenfalls eine echte Canvas-Interaktion → analog
            // zum Drag pausieren und nach 5 s Idle wieder resumen.
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            // FOV-Range strikt 60°…90° — verhindert Tiny-Planet / Fisheye-Warp.
            fovRef.current = Math.max(60, Math.min(90, fovRef.current + e.deltaY * 0.05));
            scheduleAutoResume();
            tracker.onInteraction(lonRef.current, latRef.current, fovRef.current);
        };
        const distance = (a: Touch, b: Touch) =>
            Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const onTouchStart = (e: TouchEvent) => {
            // Slider/Controls berühren die Auto-Tour nicht.
            if (shouldSkip(e.target)) return;
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            if (e.touches.length === 2) {
                pinchStart = { d: distance(e.touches[0], e.touches[1]), fov: fovRef.current };
            }
        };
        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2 && pinchStart) {
                e.preventDefault();
                const d = distance(e.touches[0], e.touches[1]);
                // FOV-Range strikt 60°…90° — verhindert Tiny-Planet / Fisheye-Warp.
                fovRef.current = Math.max(
                    60,
                    Math.min(90, pinchStart.fov * (pinchStart.d / d)),
                );
            }
        };
        const onTouchEnd = (e: TouchEvent) => {
            pinchStart = null;
            // Slider-touchend läuft durch den `shouldSkip`-Guard oben nicht
            // in diesen Zweig hinein — hier landen nur echte Canvas-Touches.
            if (!shouldSkip(e.target)) {
                scheduleAutoResume();
            }
        };

        container.addEventListener("pointerdown", onPointerDown);
        container.addEventListener("pointermove", onPointerMove);
        container.addEventListener("pointerup", onPointerUp);
        container.addEventListener("pointercancel", onPointerUp);
        container.addEventListener("wheel", onWheel, { passive: false });
        container.addEventListener("touchstart", onTouchStart, { passive: true });
        container.addEventListener("touchmove", onTouchMove, { passive: false });
        container.addEventListener("touchend", onTouchEnd);
        return () => {
            container.removeEventListener("pointerdown", onPointerDown);
            container.removeEventListener("pointermove", onPointerMove);
            container.removeEventListener("pointerup", onPointerUp);
            container.removeEventListener("pointercancel", onPointerUp);
            container.removeEventListener("wheel", onWheel);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
        };
    }, [stopAutoRotate, cancelAutoResume, scheduleAutoResume]);

    // -------- Slider Drag (separate from camera) --------
    // WICHTIG: Slider-Interaktion darf die Auto-Tour NIEMALS pausieren.
    // Der Slider steuert ausschliesslich die Split-Position. Die Kamera dreht
    // ungestört weiter — auch während der Nutzer aktiv am Trenner zieht.
    const onSliderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const container = containerRef.current;
        if (!container) return;
        const target = e.currentTarget;
        target.setPointerCapture(e.pointerId);
        const rect = container.getBoundingClientRect();
        const update = (clientX: number) => {
            const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            splitRef.current = ratio;
            setSplit(ratio);
        };
        update(e.clientX);
        const move = (ev: PointerEvent) => update(ev.clientX);
        const up = () => {
            target.removeEventListener("pointermove", move);
            target.removeEventListener("pointerup", up);
            target.removeEventListener("pointercancel", up);
        };
        target.addEventListener("pointermove", move);
        target.addEventListener("pointerup", up);
        target.addEventListener("pointercancel", up);
    }, []);

    // WebGL-Init-Fehler → statischer Fallback statt App-Crash.
    if (initError) {
        return (
            <ViewerFallback
                title="360°-Vergleich nicht verfügbar"
                message={initError}
                testId={`${testIdPrefix}-fallback`}
            />
        );
    }

    return (
        <div
            ref={containerRef}
            className="hf-viewer-shell relative w-full h-full bg-black overflow-hidden"
            data-testid={`${testIdPrefix}-shell`}
        >
            <canvas
                ref={canvasRef}
                className="block w-full h-full"
                data-testid={`${testIdPrefix}-canvas`}
            />

            {/* Slider-Line (screen-space) */}
            <div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                    left: `${split * 100}%`,
                    transform: "translateX(-50%)",
                    width: "2px",
                    background: "linear-gradient(to bottom, rgba(212,175,55,0.9), rgba(212,175,55,0.4))",
                    boxShadow: "0 0 12px rgba(212,175,55,0.6)",
                }}
                data-testid={`${testIdPrefix}-split-line`}
            />
            {/* Slider-Handle: absorbs pointer events, does NOT bubble to camera-drag */}
            <div
                className="ba360-interactive absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-ew-resize"
                style={{
                    left: `${split * 100}%`,
                    transform: `translate(-50%, -50%)`,
                    width: "44px",
                    height: "44px",
                    borderRadius: "999px",
                    background: "hsl(var(--hf-gold) / 0.9)",
                    color: "#0e1726",
                    boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
                    touchAction: "none",
                    zIndex: 4,
                    pointerEvents: "auto",
                }}
                onPointerDown={onSliderPointerDown}
                data-testid={`${testIdPrefix}-handle`}
                role="slider"
                aria-label="Vorher-Nachher-Split"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(split * 100)}
            >
                <div className="flex items-center gap-0.5 text-[10px] font-medium">
                    <span>◀</span>
                    <span>▶</span>
                </div>
            </div>

            {/* Corner-Labels — dynamisch mittig in ihrer sichtbaren Hälfte.
             *  `split` ist ein Ratio 0..1 (Slider-Position). Prozent-Rechnung:
             *  VORHER liegt mittig in der linken Hälfte (0..split) → (split*100)/2.
             *  NACHHER liegt mittig in der rechten Hälfte (split..1) →
             *  split*100 + (100-split*100)/2. Bei zu schmaler Hälfte (< 15%)
             *  wird das Label ausgeblendet, damit es nicht mit dem Handle
             *  kollidiert oder aus dem Canvas ragt. */}
            <span
                className="absolute pointer-events-none"
                style={{
                    top: "12px",
                    left: `${(split * 100) / 2}%`,
                    transform: "translateX(-50%)",
                    opacity: split * 100 > 15 ? 1 : 0,
                    transition: "left 120ms ease-out, opacity 200ms ease-out",
                    padding: "0.35rem 0.75rem",
                    background: "rgba(23,22,19,0.55)",
                    color: "rgba(255,253,249,0.95)",
                    fontFamily: "'Jost', sans-serif",
                    fontSize: "11px",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    borderRadius: "3px",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)",
                    zIndex: 3,
                }}
                data-testid={`${testIdPrefix}-label-before`}
            >
                Vorher
            </span>
            <span
                className="absolute pointer-events-none"
                style={{
                    top: "12px",
                    left: `${split * 100 + (100 - split * 100) / 2}%`,
                    transform: "translateX(-50%)",
                    opacity: split * 100 < 85 ? 1 : 0,
                    transition: "left 120ms ease-out, opacity 200ms ease-out",
                    padding: "0.35rem 0.75rem",
                    background: "rgba(23,22,19,0.55)",
                    color: "rgba(255,253,249,0.95)",
                    fontFamily: "'Jost', sans-serif",
                    fontSize: "11px",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    borderRadius: "3px",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)",
                    zIndex: 3,
                }}
                data-testid={`${testIdPrefix}-label-after`}
            >
                Nachher
            </span>

            {/*
             * KI-Badges — pro Panorama-Hälfte. Sitzt am äußeren oberen Rand
             * (Vorher: oben-links, Nachher: oben-rechts) und fadet
             * synchron zu den „VORHER"/„NACHHER"-Labels aus, wenn die
             * jeweilige Hälfte durch den Split komplett verdeckt wird.
             * Design identisch zum 2D-`BeforeAfterSlider`.
             */}
            {beforeAi && (
                <span
                    className="absolute pointer-events-none inline-flex items-center gap-1 rounded-full backdrop-blur-sm"
                    aria-label="Vorher-Panorama ist KI-generiert"
                    data-testid={`${testIdPrefix}-ai-badge-before`}
                    style={{
                        top: 12,
                        left: 12,
                        background: "rgba(23, 22, 19, 0.7)",
                        color: "#D4A574",
                        padding: "2px 5px",
                        fontSize: 8,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        border: "1px solid rgba(215, 170, 100, 0.25)",
                        boxShadow: "0 3px 8px -3px rgba(0, 0, 0, 0.35)",
                        opacity: split * 100 > 12 ? 1 : 0,
                        transition: "opacity 200ms ease",
                        zIndex: 3,
                    }}
                >
                    <span aria-hidden style={{ color: "#EBD5A0", display: "inline-flex" }}>✨</span>
                    KI
                </span>
            )}
            {afterAi && (
                <span
                    className="absolute pointer-events-none inline-flex items-center gap-1 rounded-full backdrop-blur-sm"
                    aria-label="Nachher-Panorama ist KI-generiert"
                    data-testid={`${testIdPrefix}-ai-badge-after`}
                    style={{
                        top: 12,
                        right: 12,
                        background: "rgba(23, 22, 19, 0.7)",
                        color: "#D4A574",
                        padding: "2px 5px",
                        fontSize: 8,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        border: "1px solid rgba(215, 170, 100, 0.25)",
                        boxShadow: "0 3px 8px -3px rgba(0, 0, 0, 0.35)",
                        opacity: split * 100 < 88 ? 1 : 0,
                        transition: "opacity 200ms ease",
                        zIndex: 3,
                    }}
                >
                    <span aria-hidden style={{ color: "#EBD5A0", display: "inline-flex" }}>✨</span>
                    KI
                </span>
            )}

            {/* Close (nur wenn Modal-Mode) */}
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className="ba360-interactive absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] font-medium"
                    style={{
                        background: "rgba(14,23,38,0.72)",
                        color: "#faf8f3",
                        border: "1px solid rgba(255,255,255,0.08)",
                        zIndex: 5,
                    }}
                    data-testid={`${testIdPrefix}-close`}
                >
                    <CloseIcon size={14} />
                    Schließen
                </button>
            )}

            {/* Control-Bar (Tour / Gyro / Fullscreen) */}
            <div className="hf-viewer-controls ba360-interactive">
                <div className="hf-viewer-controls-row">
                    <button
                        type="button"
                        onClick={resetView}
                        className={
                            autoRotating
                                ? "hf-viewer-ctrl-btn hf-autorotate-active"
                                : "hf-viewer-ctrl-btn"
                        }
                        title={autoRotating ? "Auto-Tour stoppen" : "Auto-Tour starten"}
                        aria-pressed={autoRotating}
                        data-testid={`${testIdPrefix}-btn-reset`}
                    >
                        <RotateCcw size={18} />
                        <span className="hf-viewer-ctrl-label">Tour</span>
                    </button>
                    {showGyro && (
                        <button
                            type="button"
                            onClick={toggleGyro}
                            className={
                                gyroOn
                                    ? "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro hf-viewer-ctrl-btn--active"
                                    : "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro"
                            }
                            title={gyroOn ? "Gyroskop aus" : "Gyroskop an"}
                            aria-pressed={gyroOn}
                            data-testid={`${testIdPrefix}-btn-gyro`}
                        >
                            <Compass size={18} />
                            <span className="hf-viewer-ctrl-label">Gyro</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="hf-viewer-ctrl-btn"
                        title={isFullscreen ? "Vollbild verlassen" : "Vollbild"}
                        data-testid={`${testIdPrefix}-btn-fullscreen`}
                    >
                        {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        <span className="hf-viewer-ctrl-label">Vollbild</span>
                    </button>
                </div>
            </div>

            {/* Loading / Error State */}
            {!loaded && !loadError && (
                <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    style={{ background: "rgba(14,23,38,0.75)", color: "#faf8f3" }}
                    data-testid={`${testIdPrefix}-loading`}
                >
                    <div className="text-xs uppercase tracking-[0.2em]">Lade 360°-Panoramen…</div>
                </div>
            )}
            {loadError && (
                <div
                    className="absolute inset-0 flex items-center justify-center px-6"
                    style={{ background: "rgba(14,23,38,0.85)", color: "#faf8f3" }}
                    data-testid={`${testIdPrefix}-error`}
                >
                    <div className="text-sm text-center">{loadError}</div>
                </div>
            )}
        </div>
    );
}
